import { Box, Grid, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { RootState } from '../../../../stores/Store';

import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import TextInput from '../../../../components/TextInput/TextInput';
import PageTitle from '../../../../components/Text/PageTitle';
import DeviceDetailsOptions from './DeviceDetailsOptions';

import {
  EidLength,
  ImeiLength,
  NumberLettersRegex,
  NumbersLettersSpacesSymbolsRegex,
} from '../../../../configs/RegexConfig';

import useModal from '../../../../hooks/useModal/useModal';

import { Device, DeviceType, DeviceUpdateRequest, Sim } from '../../../../models/UserDevicesModels';

export const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid${theme.palette.disabled.main}`,
  padding: '0.5rem',
  minHeight: '4rem',
  alignItems: 'center',
  fontSize: theme.typography.body1.fontSize,
  lineHeight: 'normal',
  '& .label': {
    color: theme.palette.text.disabled,
  },
}));

interface DeviceDetailsInfoRowsProps {
  device: Device | undefined | null;
  sims: Sim[] | [];
  isLoading: boolean;
  updateDeviceDetails: (d: DeviceUpdateRequest, setFormOpen: (val: boolean) => void) => void;
  assignSim?: () => void;
  deleteDevice: () => void;
  editMode?: boolean;
}
function DeviceDetailsInfoRows({
  device,
  sims,
  isLoading,
  updateDeviceDetails,
  assignSim,
  deleteDevice,
  editMode = false,
}: DeviceDetailsInfoRowsProps) {
  const [t] = useTranslation();
  const { companyConfigs } = useSelector((state: RootState) => state.userDetailsReducer);
  const [formOpen, setFormOpen] = useState(editMode);
  const deviceTypeOptions = Object.keys(DeviceType).map((dev) => ({
    key: dev,
    value: dev,
    label: t(`common.deviceType.${dev}`),
  }));
  const modal = useModal();

  const formik = useFormik({
    initialValues: {
      manufacturer: device?.manufacturer || '',
      model: device?.model || '',
      os: device?.os || '',
      serialNumber: device?.serialNumber || '',
      deviceType: device?.deviceType || '',
      eid: device?.eid || '',
      imeiOne: device?.imeiOne || '',
      imeiTwo: device?.imeiTwo || '',
      freeText: device?.freeText || '',
      assetTagNumber: device?.assetTagNumber || '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      manufacturer: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      model: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      os: yup
        .string()
        .notRequired()
        .matches(
          NumbersLettersSpacesSymbolsRegex,
          t('common.inputValidations.onlyNumbersAndLettersAndSpacesAndSymbols'),
        ),
      serialNumber: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .matches(NumberLettersRegex, t('common.inputValidations.onlyNumbersAndLetters')),
      eid: yup.string().when([], {
        is: () => device?.simType === 'ESIM',
        then: () =>
          yup
            .string()
            .required(t('common.inputValidations.requiredField'))
            .max(EidLength, t('common.inputValidations.maxLength', { len: EidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
        otherwise: () =>
          yup
            .string()
            .notRequired()
            .max(EidLength, t('common.inputValidations.maxLength', { len: EidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      }),
      imeiOne: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .max(ImeiLength, t('common.inputValidations.maxLength', { len: ImeiLength }))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      imeiTwo: yup
        .string()
        .max(ImeiLength, t('common.inputValidations.maxLength', { len: ImeiLength }))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      freeText: yup
        .string()
        .max(
          companyConfigs.deviceFreeTextLimit,
          t('common.inputValidations.maxLength', { len: companyConfigs.deviceFreeTextLimit }),
        ),
      assetTagNumber: yup
        .string()
        .max(
          companyConfigs.deviceAssetTagNumberLimit,
          t('common.inputValidations.maxLength', { len: companyConfigs.deviceAssetTagNumberLimit }),
        ),
    }),
    onSubmit: (values) => {
      if (device) {
        updateDeviceDetails({ id: device.id, ...values, deviceType: values.deviceType as DeviceType }, setFormOpen);
      }
    },
  });

  const ongoingOperations = useMemo(
    () => sims.some((sim) => sim.status === 'PENDING' || sim.status === 'PROCESSING'),
    [sims],
  );

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = () => {
    modal.showModal({
      modalTitle: t('pages.deviceDetails.deleteDevice'),
      modalDescription: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>{t('pages.deviceDetails.deleteDeviceMessage1')}</span>
          <span>{t('pages.deviceDetails.deleteDeviceMessage2')}</span>
        </Box>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('common.delete'),
      onAccept: (setOpen) => {
        setOpen(false);
        deleteDevice();
      },
    });
  };

  return (
    <Box>
      <Grid container>
        <Grid item xl={6} md={12} xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <PageTitle>{device ? `${device?.manufacturer} ${device?.model}` : <Skeleton width="25%" />}</PageTitle>
            <DeviceDetailsOptions
              deviceOperations={device?.deviceOperations || []}
              ongoingOperations={ongoingOperations}
              formOpen={formOpen}
              editDevice={() => setFormOpen(true)}
              assignSim={assignSim}
              deleteDevice={showConfirmationModal}
              isPerformingAction={isLoading}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container sx={{ paddingTop: '1.5rem', paddingBottom: '2.5rem', marginBottom: '2.5rem' }}>
        {!formOpen ? (
          <Grid item xl={6} md={12} xs={12}>
            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.manufacturer')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.manufacturer : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.model')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.model : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.os')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.os : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.serialNumber')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device && device.serialNumber ? device.serialNumber : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.deviceType')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? t(`translation:common.deviceType.${device.deviceType}`) : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.eid')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.eid : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.imeiOne')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.imeiOne : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            {device && device?.imeiTwo ? (
              <Row>
                <Grid container rowSpacing="0.5rem">
                  <Grid item xl={3} md={4} xs={12} className="label">
                    {t('translation:pages.deviceDetails.imeiTwo')}
                  </Grid>
                  <Grid item xl={9} md={8} xs={12}>
                    {device.imeiTwo}
                  </Grid>
                </Grid>
              </Row>
            ) : null}

            <Row>
              <Grid container rowSpacing="0.5rem">
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.assetTagNumber')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.assetTagNumber : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>

            <Row>
              <Grid container sx={{ padding: '0.844rem 0 0.844rem' }}>
                <Grid item xl={3} md={4} xs={12} className="label">
                  {t('translation:pages.deviceDetails.freeText')}
                </Grid>
                <Grid item xl={9} md={8} xs={12}>
                  {device ? device.freeText : <Skeleton width="50%" />}
                </Grid>
              </Grid>
            </Row>
          </Grid>
        ) : (
          <Grid item xl={4} md={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <TextInput
                autoComplete="off"
                key="manufacturer"
                id="manufacturer"
                name="manufacturer"
                type="text"
                label={t(`pages.deviceDetails.manufacturer`)}
                value={formik.values.manufacturer}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.manufacturer && Boolean(formik.errors.manufacturer)}
                feedback={
                  formik.touched.manufacturer && Boolean(formik.errors.manufacturer) ? formik.errors.manufacturer : ''
                }
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={
                  t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.manufacturer`).toLowerCase()
                }
              />
              <TextInput
                autoComplete="off"
                key="model"
                id="model"
                name="model"
                type="text"
                label={t(`pages.deviceDetails.model`)}
                value={formik.values.model}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.model && Boolean(formik.errors.model)}
                feedback={formik.touched.model && Boolean(formik.errors.model) ? formik.errors.model : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.model`).toLowerCase()}
              />

              <TextInput
                autoComplete="off"
                key="os"
                id="os"
                name="os"
                type="text"
                label={t(`pages.deviceDetails.os`)}
                value={formik.values.os}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.os && Boolean(formik.errors.os)}
                feedback={formik.touched.os && Boolean(formik.errors.os) ? formik.errors.os : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.os`).toLowerCase()}
              />

              <TextInput
                autoComplete="off"
                key="serialNumber"
                id="serialNumber"
                name="serialNumber"
                type="text"
                label={t(`pages.deviceDetails.serialNumber`)}
                value={formik.values.serialNumber}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.serialNumber && Boolean(formik.errors.serialNumber)}
                feedback={
                  formik.touched.serialNumber && Boolean(formik.errors.serialNumber) ? formik.errors.serialNumber : ''
                }
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={
                  t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.serialNumber`).toLowerCase()
                }
              />

              <Dropdown
                id="deviceType"
                name="deviceType"
                label={t('pages.deviceDetails.deviceType')}
                value={formik.values.deviceType}
                onChange={(e) => formik.handleChange(e)}
                onBlur={formik.handleBlur}
                formStyle={{ paddingBottom: '1.5rem' }}
                items={deviceTypeOptions}
                placeholder={
                  t('common.placeholders.dropdownPlaceholder') + t('pages.deviceDetails.deviceType').toLowerCase()
                }
              />

              <TextInput
                autoComplete="off"
                key="eid"
                id="eid"
                name="eid"
                type="text"
                label={t(`pages.deviceDetails.eid`)}
                value={formik.values.eid}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.eid && Boolean(formik.errors.eid)}
                feedback={formik.touched.eid && Boolean(formik.errors.eid) ? formik.errors.eid : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.eid`)}
              />

              <TextInput
                autoComplete="off"
                key="imeiOne"
                id="imeiOne"
                name="imeiOne"
                type="text"
                label={t(`pages.deviceDetails.imeiOne`)}
                value={formik.values.imeiOne}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.imeiOne && Boolean(formik.errors.imeiOne)}
                feedback={formik.touched.imeiOne && Boolean(formik.errors.imeiOne) ? formik.errors.imeiOne : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.imeiOne`)}
              />

              <TextInput
                autoComplete="off"
                key="imeiTwo"
                id="imeiTwo"
                name="imeiTwo"
                type="text"
                label={t(`pages.deviceDetails.imeiTwo`)}
                value={formik.values.imeiTwo}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.imeiTwo && Boolean(formik.errors.imeiTwo)}
                feedback={formik.touched.imeiTwo && Boolean(formik.errors.imeiTwo) ? formik.errors.imeiTwo : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.imeiTwo`)}
              />

              <TextInput
                autoComplete="off"
                key="assetTagNumber"
                id="assetTagNumber"
                name="assetTagNumber"
                type="text"
                label={t(`pages.deviceDetails.assetTagNumber`)}
                value={formik.values.assetTagNumber}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.assetTagNumber && Boolean(formik.errors.assetTagNumber)}
                feedback={
                  formik.touched.assetTagNumber && Boolean(formik.errors.assetTagNumber)
                    ? formik.errors.assetTagNumber
                    : ''
                }
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={
                  t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.assetTagNumber`).toLowerCase()
                }
              />

              <TextInput
                autoComplete="off"
                key="freeText"
                id="freeText"
                name="freeText"
                type="text"
                label={t(`pages.deviceDetails.freeText`)}
                value={formik.values.freeText}
                onInputChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.freeText && Boolean(formik.errors.freeText)}
                feedback={formik.touched.freeText && Boolean(formik.errors.freeText) ? formik.errors.freeText : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                placeholder={
                  t('common.placeholders.inputPlaceholder') + t(`pages.deviceDetails.freeText`).toLowerCase()
                }
                multiline
                inputStyle={{
                  '& .MuiInputBase-input': {
                    padding: '1rem',
                  },
                }}
                minRows={3}
                maxRows={3}
              />

              <ButtonGroup sx={{ paddingTop: '0.5rem' }}>
                <Button
                  label={t('common.cancel')}
                  color="alternative2Button"
                  type="submit"
                  onClick={() => {
                    setFormOpen(!formOpen);
                    formik.resetForm();
                  }}
                />
                <Button
                  label={t('common.saveChanges')}
                  color="primaryButton"
                  type="submit"
                  disabled={isLoading || sessionStorage.getItem('impersonateId') !== null}
                />
              </ButtonGroup>
            </form>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default memo(DeviceDetailsInfoRows);
